/* eslint-disable camelcase */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-nested-ternary */
import React, { useMemo, useCallback, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'

import moment from 'moment/moment'
import PropTypes from 'prop-types'

import capitalize from 'lodash/capitalize'
import filter from 'lodash/filter'
import find from 'lodash/find'
import flatMap from 'lodash/flatMap'
import groupBy from 'lodash/groupBy'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import isFinite from 'lodash/isFinite'
import isNumber from 'lodash/isNumber'
import join from 'lodash/join'
import map from 'lodash/map'
import orderBy from 'lodash/orderBy'
import sum from 'lodash/sum'
import sumBy from 'lodash/sumBy'
import toNumber from 'lodash/toNumber'

import { useT } from '@smartcoop/i18n'
import { arrowDown, arrowUp } from '@smartcoop/icons'
import { getDashboardData } from '@smartcoop/services/apis/smartcoopApi/resources/dairyFarm'
import { selectModuleIsTechnical } from '@smartcoop/stores/module/selectorModule'
import { selectCurrentProperty } from '@smartcoop/stores/property/selectorProperty'
import { selectCurrentOwner } from '@smartcoop/stores/technical/selectorTechnical'
import { selectUser } from '@smartcoop/stores/user/selectorUser'
import { colors } from '@smartcoop/styles'
import { momentBackDateTimeFormat, momentFriendlyDateFormat, momentFriendlyDateTimeFormat } from '@smartcoop/utils/dates'
import { formatCpfCnpj, formatCurrencyJs, formatNumberJs } from '@smartcoop/utils/formatters'
import Icon from '@smartcoop/web-components/Icon'
import IconButton from '@smartcoop/web-components/IconButton'

import { Container, RowHeader, CardContainer, Title, Value, Row, BiggerTitle, Divisor, Table, Column, FixedTd } from './styles'

const IncomeReport = (props) => {
  const t = useT()

  const { data, years, groupedBy, model, reportValues, isMobile } = props

  const currentProperty = useSelector(selectCurrentProperty)
  const user = useSelector(selectUser)
  const isTechnical = useSelector(selectModuleIsTechnical)
  const currentOwner = useSelector(selectCurrentOwner)

  const isMilk = model === 'milk'

  const [openedGroups, setOpenedGroups] = useState([])
  const [milkData, setMilkData] = useState([])

  const loadMilkData = useCallback(async () => {
    const { data: response } = await getDashboardData({
      date: moment().year(years[0]).format('YYYY-MM')
    },
    {
      dairyFarmId: currentProperty?.dairyFarm?.[0]?.id
    }
    )
    setMilkData(map(response?.prices, price => ({ name: moment().month(price[0]).year(years[0]).format('MMM/YY'), value: toNumber(price[1]?.monthlyMilkVolume) })))
  },[currentProperty, years])

  useEffect(() => {
    if(isMilk && !isEmpty(years)) {
      loadMilkData()
    }
  },[isMilk, loadMilkData, years])

  const totalValue = useMemo(() => sum(map(data, group => sumBy(group, val => val?.proportionalValue || val?.value || 0))), [data])

  const receitaOperacionalLiquida = useCallback((receitas, deducoes) => {
    const receitaValue = sumBy(receitas || data?.receitas, item => item?.proportionalValue || item?.value || 0)
    const deducoesValue = sumBy(deducoes || data?.deducoes, item => item?.proportionalValue || item?.value || 0)
    return receitaValue + deducoesValue
  }, [data])

  const resultadoOperacionalBruto = useCallback((receita = null, custos = null) => {
    const custosValue = sumBy(custos || data?.custos, item => item?.proportionalValue || item?.value || 0)
    return isNumber(receita) ? receita + custosValue : receitaOperacionalLiquida() + custosValue
  }, [data, receitaOperacionalLiquida])

  const resultadoAtividade = useCallback((bruto = null, despesas = null) => {
    const despesasValue = sumBy(despesas || data?.despesas, item => item?.proportionalValue || item?.value || 0)
    return isNumber(bruto) ? bruto + despesasValue : resultadoOperacionalBruto() + despesasValue
  }, [data, resultadoOperacionalBruto])

  const margemAtividade = useCallback((resAtividade = null, recOperacionalLiquida = null) => {
    const response = isNumber(resAtividade) ? resAtividade / recOperacionalLiquida : resultadoAtividade() / receitaOperacionalLiquida()

    return isFinite(response) ? response * 100 : 0
  }, [receitaOperacionalLiquida, resultadoAtividade])

  const resultadoLitro = useCallback((resAtividade = null, producao = null) => {
    const response = isNumber(resAtividade) ? resAtividade / producao : resultadoAtividade() / sum(map(milkData, price => price?.value))

    return isFinite(response) ? response : 0
  }, [milkData, resultadoAtividade])

  const resultadoLiquidoExercicio = useCallback((total = null) => total ? sumBy(total, item => item?.proportionalValue || item?.value || 0) : totalValue, [totalValue])

  const percentualReceitaLiquida = useCallback((resLiquidoExercicio = null, producao = null) => {
    const response = isNumber(resLiquidoExercicio) ? resLiquidoExercicio / producao : resultadoLiquidoExercicio() / receitaOperacionalLiquida()

    return isFinite(response) ? response * 100 : 0
  }, [receitaOperacionalLiquida, resultadoLiquidoExercicio])

  const resultadoLitroFinal = useCallback((resLiquidoExercicio = null, producao = null) => {
    const response = isNumber(resLiquidoExercicio) ? resLiquidoExercicio / producao : resultadoLiquidoExercicio() / sum(map(milkData, price => price?.value))

    return isFinite(response) ? response : 0
  }, [milkData, resultadoLiquidoExercicio])

  const outrasDespesasReceitas = useMemo(() => {
    const updatedData = []
    if(data?.outras_receitas) {
      updatedData.push(...data?.outras_receitas)
    }
    if(data?.outras_despesas) {
      updatedData.push(...data?.outras_despesas)
    }
    return updatedData
  }, [data])

  const groups = useMemo(() => {
    if(isMilk) {
      return [
        {
          label: 'PRODUÇÃO (L)',
          slug: 'producao',
          value: sum(map(milkData, price => price?.value))
        },
        {
          label: 'RECEITA OPERACIONAL BRUTA',
          slug: 'receitas',
          items: data?.receitas
        },
        {
          label: 'DEDUÇÕES DA RECEITA',
          slug: 'deducoes',
          items: data?.deducoes
        },
        {
          label: '(=) RECEITA OPERACIONAL LÍQUIDA',
          slug: 'receita-operacional-liquida',
          value: receitaOperacionalLiquida()
        },
        {
          label: 'CUSTOS DE PRODUÇÃO',
          slug: 'custos',
          items: data?.custos
        },
        {
          label: '(=) RESULTADO OPERACIONAL BRUTO',
          slug: 'resultado-operacional-bruto',
          value: resultadoOperacionalBruto()
        },
        {
          label: 'DESPESAS GERAIS',
          slug: 'despesas',
          items: data?.despesas
        },
        {
          label: 'RESULTADO DA ATIVIDADE',
          slug: 'resultado_atividade',
          value: resultadoAtividade()
        },
        {
          label: '% MARGEM ATIVIDADE',
          slug: 'margem_atividade',
          value: margemAtividade()
        },
        {
          label: 'RESULTADO POR LITRO',
          slug: 'resultado_litro',
          value: resultadoLitro()
        },
        {
          label: 'RESULTADO FINANCEIRO',
          slug: 'resultado_financeiro',
          items: data?.resultado_financeiro
        },
        {
          label: 'OUTRAS RECEITAS E DESPESAS',
          slug: 'outras_receitas_despesas',
          items: outrasDespesasReceitas
        },
        {
          label: 'RESULTADO NÃO OPERACIONAL',
          slug: 'resultado_nao_operacional',
          items: data?.resultado_nao_operacional
        },
        {
          label: 'IMPOSTO DE RENDA E CONTRIBUIÇÃO SOCIAL',
          slug: 'IR_E_CSLL',
          items: data?.IR_E_CSLL
        },
        {
          label: `(=) ${ t('exercise liquid result') }`,
          slug: 'resultado_liquido_exercicio',
          value: resultadoLiquidoExercicio()
        },
        {
          label: '% SOBRE A RECEITA LIQUIDA',
          slug: 'percentual_receita_liquida',
          value: percentualReceitaLiquida()
        },
        {
          label: 'RESULTADO POR LITRO',
          slug: 'resultado_litro_final',
          value: resultadoLitroFinal()
        }
      ]
    }

    return[{
      label: 'RECEITAS',
      slug: 'receitas',
      items: data?.receitas
    },
    {
      label: 'DEDUÇÕES DA RECEITA',
      slug: 'deducoes',
      items: data?.deducoes
    },
    {
      label: 'CUSTOS DE PRODUÇÃO',
      slug: 'custos',
      items: data?.custos
    },
    {
      label: 'DESPESAS GERAIS',
      slug: 'despesas',
      items: data?.despesas
    },
    {
      label: 'RESULTADO FINANCEIRO',
      slug: 'resultado_financeiro',
      items: data?.resultado_financeiro
    },
    {
      label: 'OUTRAS RECEITAS E DESPESAS',
      slug: 'outras_receitas_despesas',
      items: outrasDespesasReceitas
    },
    {
      label: 'RESULTADO NÃO OPERACIONAL',
      slug: 'resultado_nao_operacional',
      items: data?.resultado_nao_operacional
    },
    {
      label: 'IMPOSTO DE RENDA E CONTRIBUIÇÃO SOCIAL',
      slug: 'IR_E_CSLL',
      items: data?.IR_E_CSLL
    }
    ]
  },[data, isMilk, margemAtividade, milkData, outrasDespesasReceitas, percentualReceitaLiquida, receitaOperacionalLiquida, resultadoAtividade, resultadoLiquidoExercicio, resultadoLitro, resultadoLitroFinal, resultadoOperacionalBruto, t])

  useEffect(() => {
    setOpenedGroups(map(groups, item => item.slug))
  }, [groups])

  const headerActivities = useMemo(() => {
    const headers = []
    map(groups, group => map(group?.items, item  => {
      if(item?.activity?.name && !includes(headers, item?.activity?.name)) {
        headers.push(item?.activity?.name)
      }
    }))
    return headers
  },[groups])

  const headerCrops = useMemo(() => {
    const headers = []
    map(groups, group => map(group?.items, item =>  {
      if(item?.crop?.description && item?.sowingYear && !includes(headers, `${ item?.crop?.description }/${ item?.sowingYear }`)) {
        headers.push(`${ item?.crop?.description }/${ item?.sowingYear }`)
      }
    }))
    return headers
  },[groups])

  const headerDates = useMemo(() => {
    const headers = []
    if(isMilk) {
      map(milkData, item => {
        headers.push(item?.name)
      })
    } else {
      map(groups, group => map(group?.items, item =>  {
        if(item?.realizationDate && !includes(headers, moment(item?.realizationDate, momentBackDateTimeFormat).format('MMM/YY'))) {
          headers.push(moment(item?.realizationDate, momentBackDateTimeFormat).format('MMM/YY'))
        }
      }))
    }
    return orderBy(headers, o => moment(o, 'MMM/YY'))
  },[groups, isMilk, milkData])

  const currentValue = useCallback((slug, type, value) => {
    let receitas = []
    let deducoes = []
    let custos = []
    let despesas = []
    let total = []

    if(type === 'activity') {
      const activityValues = filter(flatMap(groups, group => flatMap(group.items, item => ({ name: item?.activity?.name, value: item?.proportionalValue || item?.value  }))), activity => activity?.name === value)
      total = [...activityValues]
    }
    if(type === 'crop') {
      const cropValues = filter(flatMap(groups, group => flatMap(group.items, item =>  ({ name: `${ item?.crop?.description }/${ item?.sowingYear }`, value: item?.proportionalValue || item?.value || 0  }))), crop => crop?.name === value)
      total = [...cropValues]
    }
    if(type === 'date') {
      const dateValues = filter(flatMap(groups, group => flatMap(group.items, item =>  ({ name: moment(item?.realizationDate, momentBackDateTimeFormat).format('MMM/YY'), value: item?.proportionalValue || item?.value || 0  }))), date => date?.name === value)
      total = [...dateValues]
    }
    map(groups, group => {
      if(type === 'activity') {
        const activityValues = filter(flatMap(group.items, item => ({ name: item?.activity?.name, value: item?.proportionalValue || item?.value || 0  })), activity => activity?.name === value)
        if(group?.slug === 'receitas') {
          receitas = [...activityValues]
        }
        if(group?.slug === 'deducoes') {
          deducoes = [...activityValues]
        }
        if(group?.slug === 'custos') {
          custos = [...activityValues]
        }
      } else if(type === 'crop') {
        const cropValues = filter(flatMap(group.items, item =>  ({ name: `${ item?.crop?.description }/${ item?.sowingYear }`, value: item?.proportionalValue || item?.value || 0  })), crop => crop?.name === value)
        if(group?.slug === 'receitas') {
          receitas = [...cropValues]
        }
        if(group?.slug === 'deducoes') {
          deducoes = [...cropValues]
        }
        if(group?.slug === 'custos') {
          custos = [...cropValues]
        }
      } else if(type === 'date') {
        const dateValues = filter(flatMap(group.items, item =>  ({ name: moment(item?.realizationDate, momentBackDateTimeFormat).format('MMM/YY'), value: item?.proportionalValue || item?.value || 0  })), date => date?.name === value)
        if(group?.slug === 'receitas') {
          receitas = [...dateValues]
        }
        if(group?.slug === 'deducoes') {
          deducoes = [...dateValues]
        }
        if(group?.slug === 'custos') {
          custos = [...dateValues]
        }
        if(group?.slug === 'despesas') {
          despesas = [...dateValues]
        }
      }
    })
    if(slug === 'receita-operacional-liquida') {
      return receitaOperacionalLiquida(receitas, deducoes)
    }
    if(slug === 'resultado-operacional-bruto') {
      return resultadoOperacionalBruto(receitaOperacionalLiquida(receitas, deducoes), custos)
    }
    if(slug === 'resultado_atividade') {
      return resultadoAtividade(resultadoOperacionalBruto(receitaOperacionalLiquida(receitas, deducoes), custos), despesas)
    }
    if(slug === 'margem_atividade') {
      return margemAtividade(resultadoAtividade(resultadoOperacionalBruto(receitaOperacionalLiquida(receitas, deducoes), custos), despesas), receitaOperacionalLiquida(receitas, deducoes))
    }
    if(slug === 'resultado_litro') {
      return resultadoLitro(resultadoAtividade(resultadoOperacionalBruto(receitaOperacionalLiquida(receitas, deducoes), custos), despesas), find(milkData, item => item?.name === value)?.value)
    }
    if(slug === 'resultado_liquido_exercicio') {
      return resultadoLiquidoExercicio(total)
    }
    if(slug === 'percentual_receita_liquida') {
      return percentualReceitaLiquida(resultadoLiquidoExercicio(total), receitaOperacionalLiquida(receitas, deducoes))
    }
    if(slug === 'resultado_litro_final') {
      return resultadoLitroFinal(resultadoLiquidoExercicio(total), find(milkData, item => item?.name === value)?.value)
    }
    return 0
  }, [groups, margemAtividade, milkData, percentualReceitaLiquida, receitaOperacionalLiquida, resultadoAtividade, resultadoLiquidoExercicio, resultadoLitro, resultadoLitroFinal, resultadoOperacionalBruto])

  return (
    <Container>
      <RowHeader class="row-header">
        <img
          alt="logo"
          src="data:image/png;base64,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"
        />
        <Column>
          <BiggerTitle>
            {t('income statement for the year')}
          </BiggerTitle>
          <span style={ { paddingTop: 10 } }>{t('competency')}: {join(years, ', ')}</span>
        </Column>
        <p>
          {moment().format(momentFriendlyDateFormat)}
        </p>
      </RowHeader>
      <CardContainer>
        <BiggerTitle>IDENTIFICAÇÃO DA PROPRIEDADE</BiggerTitle>
        <Divisor />
        <Row>
          <Title>
            {t('producer', { howMany: 1, gender: 'male' })}:
          </Title>
          <Value>
            { isTechnical || reportValues?.owner?.name ? reportValues?.owner?.name || currentOwner?.name : reportValues?.user?.name || user?.name }
          </Value>
        </Row>
        <Row style={ { width: '100%' } }>
          <Row style={ { marginLeft: 0, width: '50%' } }>
            <Title style={ { marginLeft: 0 } }>
              {t('property', { howMany: 1 })}:
            </Title>
            <Value style={ { marginLeft: 0 } }>
              {reportValues?.property?.name || currentProperty?.name}
            </Value>
          </Row>
          <Row style={ { marginLeft: 0,  width: '50%' } }>
            <Title>
              {t('city', { howMany: 1 })}:
            </Title>
            <Value>
              {reportValues?.property?.city || currentProperty?.city}/{reportValues?.property?.state || currentProperty?.state}
            </Value>
          </Row>
        </Row>
        {!isEmpty(reportValues?.property?.familyGroupUsers || currentProperty?.familyGroupUsers) && (
          <>
            <Row style={ { marginTop: 10 } }>
              <Title style={ { width: '100%' } }>
                {t('family group members')}
              </Title>
            </Row>
            {map(reportValues?.property?.familyGroupUsers || currentProperty?.familyGroupUsers, item => (
              <Row key={ item?.id } style={ { marginLeft: 40 } }>
                <Value>
                  {formatCpfCnpj(item?.user?.document)} - {item?.user?.name}
                </Value>
              </Row>
            ))}
          </>
        )}
      </CardContainer>
      <Table width="96%">
        <thead>
          <th style={ { textTransform: 'uppercase', textAlign: 'left', borderBottom: `1px solid ${ colors.secondary }` } }>{model === 'milk' ? `DRE ${ t('milk pecuary') }` : `${ t('demonstrative') } ${ t('full') }`}</th>
          {groupedBy === 'activity' ? map(headerActivities, header => (<th>{header}</th>)) : null }
          {groupedBy === 'crop' ? map(headerCrops, header => (<th>{header}</th>)) : null }
          {groupedBy === 'date' ? map(headerDates, header => (<th>{capitalize(header)}</th>)) : null }
          <th>Geral</th>
          <th style={ { width: '2%', maxWidth: '2%', minWidth: '2%' } }/>
        </thead>
        {map(groups, (group) => {
          const groupedData = groupBy(group?.items, 'rubricItem.id')

          const groupedValues = map(groupedData, item => ({
            description: item?.[0]?.rubricItem?.description,
            value: sumBy(item, val => val?.proportionalValue || val?.value || 0),
            abs: Math.abs(sumBy(item, val => val?.proportionalValue || val?.value || 0)),
            crops: map(groupBy(item, subItem => `${ subItem?.crop?.description }/${ subItem?.sowingYear }`), (values, index) => ({ label: index, value: sumBy(values, val => val?.proportionalValue || val?.value || 0) })),
            activities: flatMap(item, subItem => ({ name: subItem?.activity?.name, value: subItem?.proportionalValue || subItem?.value || 0  })),
            date: orderBy(map(groupBy(item, subItem => moment(subItem?.realizationDate, momentBackDateTimeFormat).format('MMM/YY')), (values, index) => ({ label: index, value: sumBy(values, val => val?.proportionalValue || val?.value || 0) })), (o) => moment(o?.label, 'MMM/YY'))
          }))

          return (
            <>
              <tr>
                <FixedTd isMobile={ isMobile } style={ { fontWeight: 'bold', paddingTop: 10 } }>
                  {group?.label}
                </FixedTd>
                {groupedBy === 'activity' ? map(headerActivities, activity => (
                  <td style={ { fontWeight: 'bold', paddingTop: 10, textAlign: 'right', color: (group?.value ? currentValue(group?.slug, 'activity', activity) : sumBy(filter(flatMap(groupedValues, val => val?.activities), val => val?.name === activity), val => val?.proportionalValue || val?.value)) < 0 ? colors.error : colors.text } }>
                    {formatCurrencyJs(group?.value ? currentValue(group?.slug, 'activity', activity) : sumBy(filter(flatMap(groupedValues, val => val?.activities), val => val?.name === activity), val => val?.proportionalValue || val?.value || 0) )}
                  </td>
                )) : null }
                {groupedBy === 'crop' ? map(headerCrops, crop => (
                  <td style={ { fontWeight: 'bold', paddingTop: 10, textAlign: 'right', color: (group?.value ? currentValue(group?.slug, 'crop', crop) : sumBy(filter(flatMap(groupedValues, val => val?.crops), val => val.label === crop), val => val?.proportionalValue || val?.value || 0)) < 0 ? colors.error : colors.text } }>
                    {formatCurrencyJs(group?.value ? currentValue(group?.slug, 'crop', crop)  : sumBy(filter(flatMap(groupedValues, val => val?.crops), val => val.label === crop), val => val?.proportionalValue || val?.value || 0))}
                  </td>
                )) : null }
                {groupedBy === 'date' ? map(headerDates, date => (
                  <td style={ { fontWeight: 'bold', paddingTop: 10, textAlign: 'right', color: (group?.value ? currentValue(group?.slug, 'date', date) : sumBy(filter(flatMap(groupedValues, val => val?.date), val => val.label === date), val => val?.proportionalValue || val?.value || 0)) < 0 ? colors.error : colors.text } }>
                    {
                      group?.slug === 'margem_atividade' || group?.slug === 'percentual_receita_liquida'
                        ? formatNumberJs(currentValue(group?.slug, 'date', date), 1)
                        : group?.slug === 'producao'
                          ? formatNumberJs(find(milkData, item => item?.name === date)?.value, 0)
                          : formatCurrencyJs(
                            group?.value
                              ? currentValue(group?.slug, 'date', date)
                              : sumBy(filter(flatMap(groupedValues, val => val?.date), val => val.label === date), val => val?.proportionalValue || val?.value || 0)
                          )
                    }
                    {group?.slug === 'margem_atividade' || group?.slug === 'percentual_receita_liquida' ? ' %' : ''}
                  </td>
                )) : null }
                <td style={ { paddingTop: 10, textAlign: 'right', fontWeight: 'bold', color: groups?.value ? groups?.value < 0 ? colors.error : colors.text : sumBy(group?.items, val => val?.proportionalValue || val?.value || 0) < 0 ? colors.error : colors.text } }>
                  {
                    group?.slug === 'margem_atividade' || group?.slug === 'percentual_receita_liquida'
                      ? formatNumberJs(group?.value, 1)
                      : group?.slug === 'producao'
                        ? formatNumberJs(group?.value, 0)
                        : formatCurrencyJs(group?.value || sumBy(group?.items, val => val?.proportionalValue || val?.value || 0))
                  }
                  {group?.slug === 'margem_atividade' || group?.slug === 'percentual_receita_liquida' ? ' %' : ''}
                </td>
                <td style={ { paddingTop: 10, width: '2%' } }>
                  {!group?.value && !isEmpty(group.items) ? (
                    <IconButton
                      size="small"
                      onClick={ () => {
                        setOpenedGroups(old => {
                          const current = [...old]
                          const found = includes(old, group?.slug)
                          if(found) {
                            return filter(current, item => item !== group?.slug)
                          }
                          return [...current, group?.slug]
                        })
                      } }
                    >
                      <Icon icon={ includes(openedGroups, group?.slug) ? arrowUp : arrowDown } color={ colors.text } />
                    </IconButton>
                  ) : null}
                </td>
              </tr>
              {group?.value || !includes(openedGroups, group?.slug) ? null :
                map(orderBy(groupedValues, 'abs', 'desc'), item => (
                  <tr>
                    <FixedTd isMobile={ isMobile } style={ { paddingLeft: 20 } }>
                      {item?.description}
                    </FixedTd>
                    {groupedBy === 'activity' ? map(headerActivities, activity => {
                      const filteredData = filter(item?.activities, register => register.name === activity)
                      return sumBy(filteredData, val => val?.proportionalValue || val?.value || 0) ? (
                        <td style={ { textAlign: 'right' } }>
                          <span style={ { color: sumBy(filteredData, val => val?.proportionalValue || val?.value || 0) < 0 ? colors.error : colors.text } }>
                            {formatCurrencyJs(sumBy(filteredData, val => val?.proportionalValue || val?.value || 0))}
                          </span>
                        </td>
                      ): <td />
                    }) : null}
                    {groupedBy === 'crop' ? map(headerCrops, crop => {
                      const filteredData = filter(item?.crops, register => register.label === crop)
                      return sumBy(filteredData, val => val?.proportionalValue || val?.value || 0) ? (
                        <td style={ { textAlign: 'right' } }>
                          <span style={ { color: sumBy(filteredData, val => val?.proportionalValue || val?.value || 0) < 0 ? colors.error : colors.text } }>
                            {formatCurrencyJs(sumBy(filteredData, val => val?.proportionalValue || val?.value || 0))}
                          </span>
                        </td>
                      ): <td/>
                    }) : null }
                    {groupedBy === 'date' ? map(headerDates, date => {
                      const filteredData = filter(item?.date, register => register.label === date)
                      return sumBy(filteredData, val => val?.proportionalValue || val?.value || 0) ? (
                        <td style={ { textAlign: 'right' } }>
                          <span style={ { color: sumBy(filteredData, val => val?.proportionalValue || val?.value || 0) < 0 ? colors.error : colors.text } }>
                            {formatCurrencyJs(sumBy(filteredData, val => val?.proportionalValue || val?.value || 0))}
                          </span>
                        </td>
                      ): <td/>
                    }) : null }
                    <td style={ { textAlign: 'right' } }>
                      <span style={ { color: item?.value < 0 ? colors.error : colors.text } }>
                        {formatCurrencyJs(item?.value)}
                      </span>
                    </td>
                    <td />
                  </tr>
                )
                )}
            </>
          )
        })}
      </Table>
      <div style={ { alignSelf: 'flex-end', fontSize: 10, marginTop: 10 } }>
        {`${ t('generated by') }: ${ reportValues?.user?.name || user?.name } ${ moment().format(momentFriendlyDateTimeFormat) }`}
      </div>
    </Container>
  )
}

IncomeReport.propTypes = {
  data: PropTypes.array.isRequired,
  years: PropTypes.array.isRequired,
  reportValues: PropTypes.object,
  groupedBy: PropTypes.string,
  isMobile: PropTypes.bool,
  model: PropTypes.string
}

IncomeReport.defaultProps = {
  reportValues: null,
  isMobile: false,
  groupedBy: null,
  model: null
}

export default IncomeReport
